/**
 * GalleryBentoView
 *
 * Dynamische Bento-Grid Ansicht für die Gallery.
 */

import { GalleryImageCard } from '../cards/GalleryImageCard';
import { cn } from '@/lib/utils';
import type { GalleryImage } from '@/lib/api/gallery-v2';

interface GalleryBentoViewProps {
  images: GalleryImage[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onImageClick?: (image: GalleryImage) => void;
  showApprovalActions?: boolean;
}

export function GalleryBentoView({
  images,
  selectedIds,
  onToggleSelect,
  onToggleFavorite,
  onImageClick,
  showApprovalActions = false,
}: GalleryBentoViewProps) {
  return (
    <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 auto-rows-[100px]">
      {images.map((image, index) => {
        // Dynamische Größe basierend auf Index und Ratio
        const size = getBentoSize(image, index);

        return (
          <div
            key={image.id}
            className={cn(
              'relative group',
              size.colSpan,
              size.rowSpan
            )}
          >
            <GalleryImageCard
              image={image}
              isSelected={selectedIds.has(image.id)}
              onToggleSelect={() => onToggleSelect(image.id)}
              onToggleFavorite={() => onToggleFavorite(image.id)}
              onClick={() => onImageClick?.(image)}
              variant="bento"
              showApprovalActions={showApprovalActions}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Berechnet die Bento-Grid-Größe basierend auf Bildverhältnis und Position.
 */
function getBentoSize(
  image: GalleryImage,
  index: number
): { colSpan: string; rowSpan: string } {
  const ratio = image.ratio || '1:1';

  // Parse ratio
  const [w, h] = ratio.split(':').map(Number);
  const aspectRatio = w / h;

  // Pattern für dynamische Größen
  const patterns = [
    // Große Bilder alle paar Positionen
    { condition: index % 7 === 0, colSpan: 'col-span-2', rowSpan: 'row-span-2' },
    // Portrait-Bilder
    {
      condition: aspectRatio < 0.8,
      colSpan: 'col-span-1',
      rowSpan: 'row-span-2',
    },
    // Landscape-Bilder
    {
      condition: aspectRatio > 1.5,
      colSpan: 'col-span-2',
      rowSpan: 'row-span-1',
    },
    // Standard
    { condition: true, colSpan: 'col-span-1', rowSpan: 'row-span-1' },
  ];

  const match = patterns.find((p) => p.condition);
  return match || { colSpan: 'col-span-1', rowSpan: 'row-span-1' };
}
